/**
 * Subscription cart-conflict popup flow.
 *
 * Intercepts add-to-cart actions in the capture phase to detect a subscription vs non-subscription conflict in cart
 * Product-page: intercepts click on .single_add_to_cart_button (inside form.cart)
 * Listing-page: intercepts click on .add_to_cart_button (outside form.cart)
 *
 * When a conflict exists the user is shown a confirmation modal. On confirmation the current cart is saved to a
 * PHP session key ("gt_saved_cart") and cleared via a single AJAX call, then the original action is re-triggered
 * via btn.click() so every handler (custom code, WC variation check, form submit) fires normally
 *
 * After a conflict confirmation the user is redirected to the cart page
 *
 * A "restore cart" button is shown by PHP (GT_Cart_Hooks) on the cart page and
 * the cart is also auto-restored after order completion (woocommerce_thankyou)
 */
export default function ($) {
  const config = window.gtCartConflict;
  if (!config) return;

  const {ajaxUrl, nonce, cartUrl, i18n} = config;

  // ── PHP-rendered modal elements ───────────────────────────────────────────

  const overlay = document.getElementById('gt-csc-overlay');
  const modal = document.getElementById('gt-csc-modal');
  const msgEl = document.getElementById('gt-csc-message');
  const btnCancel = document.getElementById('gt-csc-cancel');
  const btnConfirm = document.getElementById('gt-csc-confirm');

  if (!overlay || !modal || !msgEl || !btnCancel || !btnConfirm) return;

  // ── Modal open / close ────────────────────────────────────────────────────

  let pendingConfirm = null;
  let pendingCancel = null;

  function openModal(text, onConfirm, onCancel) {
    msgEl.textContent = text;
    pendingConfirm = onConfirm;
    pendingCancel = onCancel ?? null;
    overlay.classList.remove('hidden');
    modal.classList.remove('hidden');
    btnConfirm.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function closeModal() {
    overlay.classList.add('hidden');
    modal.classList.add('hidden');
    pendingConfirm = null;
    pendingCancel = null;
    document.removeEventListener('keydown', onKeydown);
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      const cb = pendingCancel;
      closeModal();
      cb?.();
    }
  }

  btnConfirm.addEventListener('click', () => {
    const cb = pendingConfirm;
    closeModal();
    cb?.();
  });
  btnCancel.addEventListener('click', () => {
    const cb = pendingCancel;
    closeModal();
    cb?.();
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      const cb = pendingCancel;
      closeModal();
      cb?.();
    }
  });

  // ── Cart state ────────────────────────────────────────────────────────────
  //
  // Read from the PHP-rendered #gt-cart-conflict-state element (footer).
  // WooCommerce replaces this element via fragments after every AJAX add-to-cart,
  // so the state stays accurate without any extra AJAX call.

  let cartHasSubscription = false;
  let cartHasRegular = false;
  let cartSubscriptionProductIds = [];

  function syncCartState() {
    const el = document.getElementById('gt-cart-conflict-state');
    if (!el) return;
    cartHasSubscription = el.dataset.hasSubscription === '1';
    cartHasRegular = el.dataset.hasRegular === '1';
    const raw = el.dataset.subscriptionProductIds || '';
    cartSubscriptionProductIds = raw ? raw.split(',').map(Number) : [];
  }

  syncCartState();
  $(document.body).on('added_to_cart wc_fragments_refreshed', syncCartState);

  // ── Conflict detection ────────────────────────────────────────────────────

  function getConflict(isSubscription, productId = 0) {
    if (!isSubscription && cartHasSubscription) {
      return {conflict: true, message: i18n.conflict_cart_has_subscription};
    }
    if (isSubscription && cartHasRegular) {
      return {conflict: true, message: i18n.conflict_cart_has_regular};
    }
    if (isSubscription && cartHasSubscription && !cartSubscriptionProductIds.includes(productId)) {
      return {conflict: true, message: i18n.conflict_cart_has_subscription_adding_subscription};
    }
    return {conflict: false};
  }

  // ── AJAX helpers ──────────────────────────────────────────────────────────

  function checkConflict(isSubscription) {
    return $.ajax({
      url: ajaxUrl,
      type: 'POST',
      data: {
        action: 'gt_check_subscription_conflict',
        nonce,
        is_subscription: isSubscription ? 1 : 0,
      },
    });
  }

  function saveCartAndClear() {
    return $.ajax({
      url: ajaxUrl,
      type: 'POST',
      data: {action: 'gt_save_cart_and_clear', nonce},
    });
  }

  // ── Re-dispatch helpers ───────────────────────────────────────────────────

  // Product-page button: use native btn.click() which triggers the element's
  // full activation behavior (form submission), preserving WC variation checks
  // and any custom subscription code bound to the click event.
  function redispatchProductPageBtn(btn) {
    btn.dataset.gtConflictChecked = '1';
    btn.click();
    delete btn.dataset.gtConflictChecked;
  }

  function redispatchProductPageBtnWithRedirect(btn, form) {
    let input = form.querySelector('input[name="gt_redirect_to_cart"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'gt_redirect_to_cart';
      form.appendChild(input);
    }
    input.value = '1';
    redispatchProductPageBtn(btn);
  }

  // AJAX listing-page buttons: re-dispatch a MouseEvent so jQuery delegated
  // handlers and WC's AJAX add-to-cart handler fire normally.
  function redispatchClick(btn) {
    btn.dataset.gtConflictChecked = '1';
    btn.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true}));
    delete btn.dataset.gtConflictChecked;
  }

  // ── Product-page add-to-cart button click (capture phase) ─────────────────
  //
  // Intercept the click on .single_add_to_cart_button (inside form.cart) so
  // that the conflict check runs before the form is submitted.  On re-dispatch
  // (gtConflictChecked set) we return early so the click propagates normally
  // through WC variation validation, custom subscription code, and ultimately
  // triggers form submission.

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.single_add_to_cart_button');
    if (!btn) return;
    const form = btn.closest('form.cart');
    if (!form) return;
    if (btn.dataset.gtConflictChecked) return;

    const productId = parseInt(
        form.querySelector('[name="product_id"]')?.value ||
        form.querySelector('[name="add-to-cart"]')?.value || '0'
    );
    if (!productId) return;

    const schemeEl = form.querySelector('.js-gt-convert-to-sub, .js-gt-convert-to-sub-var');
    const isSubscription = !!schemeEl && schemeEl.value !== '0' && schemeEl.value !== '';

    const result = getConflict(isSubscription, productId);
    if (!result.conflict) return; // let click propagate normally

    e.preventDefault();
    e.stopPropagation();

    openModal(result.message, () => {
      saveCartAndClear().always(() => {
        cartHasSubscription = false;
        cartHasRegular = false;
        redispatchProductPageBtnWithRedirect(btn, form);
      });
    });
  }, {capture: true});

  // ── Listing-page helpers ──────────────────────────────────────────────────

  // True for plain <a href="?add-to-cart=…"> buttons (card component, etc.).
  function isDirectLink(btn) {
    return btn.tagName === 'A' && !!btn.href && !btn.classList.contains('ajax_add_to_cart');
  }

  function proceedAddToCart(btn) {
    if (isDirectLink(btn)) {
      window.location.href = btn.href; // navigate directly; WC processes on server
    } else {
      redispatchClick(btn);
    }
  }

  function openConflictModal(message, btn) {
    openModal(message, () => {
      saveCartAndClear().always(() => {
        cartHasSubscription = false;
        cartHasRegular = false;
        if (isDirectLink(btn)) {
          window.location.href = btn.href;
        } else {
          $(document.body).one('added_to_cart.gt_csc', () => {
            window.location.href = cartUrl;
          });
          redispatchClick(btn);
        }
      });
    });
  }

  // ── Add-to-cart buttons on listing pages ──────────────────────────────────
  //
  // Targets WC standard .add_to_cart_button AND custom [data-gt-add-to-cart]
  // direct-link buttons (card component). Uses an AJAX conflict check (live
  // server session) to avoid stale DOM state on cached pages. Falls back to
  // the sync DOM check if AJAX is unavailable.

  document.addEventListener('click', function (e) {
    const btn = e.target.closest(
        '[data-gt-add-to-cart]:not(.disabled), .add_to_cart_button:not(.disabled):not(.loading)'
    );
    if (!btn) return;
    if (btn.closest('form.cart')) return;
    if (btn.dataset.gtConflictChecked) return;

    const productId = btn.dataset.gtAddToCart || btn.dataset.product_id;
    if (!productId) return;

    e.preventDefault();
    e.stopPropagation();

    checkConflict(false)
        .then((response) => {
          if (!response.success || !response.data.conflict) {
            proceedAddToCart(btn);
            return;
          }
          openConflictModal(response.data.message, btn);
        })
        .fail(() => {
          const fallback = getConflict(false);
          if (!fallback.conflict) {
            proceedAddToCart(btn);
            return;
          }
          openConflictModal(fallback.message, btn);
        });
  }, {capture: true});

  // ── Restore saved cart ────────────────────────────────────────────────────

  $(document).on('click', '.gt-restore-saved-cart-btn', function (e) {
    e.preventDefault();
    const $btn = $(this);
    $btn.prop('disabled', true).text(i18n.restoring || '…');

    $.ajax({
      url: ajaxUrl,
      type: 'POST',
      data: {action: 'gt_restore_saved_cart', nonce},
    })
        .then(() => {
          window.location.href = cartUrl;
        })
        .fail(() => {
          $btn.prop('disabled', false).text(i18n.restore_cart || 'Récupérer mon panier');
        });
  });
}
